import { useState, useRef, useEffect, useCallback, useMemo } from 'react'

/**
 * StitchPreview — PES/DST stitch visualizer and jump stitch editor
 * 
 * Replaces the canvas view after export. Shows:
 * - Thread-colored stitch paths
 * - Red dashed lines for jump stitches  
 * - Red dots for trim points
 * - Click to select/delete jumps
 * - Stats panel with stitch count, trims, estimated time
 * - Zoom/pan controls
 * 
 * Props:
 *   previewData: { shapes, jumps, trims, colors, bounds, stats }
 *   pesBase64: base64-encoded PES file for download
 *   filename: download filename
 *   onClose: callback to return to canvas view
 *   onReExport: callback with edited stitch data for re-export
 */
export default function StitchPreview({ 
  previewData, 
  pesBase64, 
  filename = 'zenna-design.pes',
  onClose,
  onReExport 
}) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const [selectedJump, setSelectedJump] = useState(null)
  const [deletedJumps, setDeletedJumps] = useState(new Set())
  const [showJumps, setShowJumps] = useState(true)
  const [showTrims, setShowTrims] = useState(true)
  const [showGrid, setShowGrid] = useState(false)
  const [hoveredJump, setHoveredJump] = useState(null)
  const [fabricColor, setFabricColor] = useState('#f5f0e8') // user-selectable fabric background
  const [isPlaying, setIsPlaying] = useState(false)
  const [playProgress, setPlayProgress] = useState(1.0) // 0.0 to 1.0 (1.0 = show all)
  const [playSpeed, setPlaySpeed] = useState(1) // 1x, 2x, 5x, 10x
  const animFrameRef = useRef(null)
  const playStartRef = useRef(null)

  const { shapes, jumps, trims, colors, bounds, stats } = previewData || {}

  // Calculate transform to fit design in canvas
  const getTransform = useCallback((canvasWidth, canvasHeight) => {
    if (!bounds) return { scale: 1, offsetX: 0, offsetY: 0 }

    const designW = bounds.maxX - bounds.minX
    const designH = bounds.maxY - bounds.minY
    if (designW === 0 || designH === 0) return { scale: 1, offsetX: 0, offsetY: 0 }

    const padding = 40
    const availW = canvasWidth - padding * 2
    const availH = canvasHeight - padding * 2

    const scale = Math.min(availW / designW, availH / designH) * zoom
    const offsetX = (canvasWidth - designW * scale) / 2 - bounds.minX * scale + pan.x
    const offsetY = (canvasHeight - designH * scale) / 2 - bounds.minY * scale + pan.y

    return { scale, offsetX, offsetY }
  }, [bounds, zoom, pan])

  // Convert design coords to canvas coords
  const toCanvas = useCallback((x, y, transform) => {
    return {
      x: x * transform.scale + transform.offsetX,
      y: y * transform.scale + transform.offsetY
    }
  }, [])

  // Convert canvas coords back to design coords (for click detection)
  const toDesign = useCallback((cx, cy, transform) => {
    return {
      x: (cx - transform.offsetX) / transform.scale,
      y: (cy - transform.offsetY) / transform.scale
    }
  }, [])

  // Total stitch count across all shapes (for player progress)
  const totalStitchPoints = useMemo(() => {
    if (!shapes) return 0
    let total = 0
    for (const shape of shapes) {
      for (const seg of shape.segments) {
        if (seg.type === 'stitch') total += seg.points.length
      }
    }
    return total
  }, [shapes])

  // Stitch player animation loop
  useEffect(() => {
    if (!isPlaying) return
    playStartRef.current = performance.now()
    const startProgress = playProgress >= 1.0 ? 0.0 : playProgress // restart from 0 if at end

    const animate = (now) => {
      const elapsed = (now - playStartRef.current) / 1000 // seconds
      // Speed: show N stitches per second (base: 800 stitches/sec at 1x)
      const stitchesPerSec = 800 * playSpeed
      const stitchesElapsed = elapsed * stitchesPerSec
      const newProgress = Math.min(1.0, startProgress + stitchesElapsed / Math.max(1, totalStitchPoints))

      setPlayProgress(newProgress)

      if (newProgress < 1.0) {
        animFrameRef.current = requestAnimationFrame(animate)
      } else {
        setIsPlaying(false)
      }
    }

    animFrameRef.current = requestAnimationFrame(animate)
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    }
  }, [isPlaying, playSpeed, totalStitchPoints])

  // Main render
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container || !shapes) return

    const rect = container.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    canvas.style.width = rect.width + 'px'
    canvas.style.height = rect.height + 'px'

    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)

    const cw = rect.width
    const ch = rect.height
    const transform = getTransform(cw, ch)

    // --- Background ---
    ctx.fillStyle = '#1a1a2e'
    ctx.fillRect(0, 0, cw, ch)

    // --- Fabric area (white rectangle showing design bounds) ---
    if (bounds) {
      const tl = toCanvas(bounds.minX - 20, bounds.minY - 20, transform)
      const br = toCanvas(bounds.maxX + 20, bounds.maxY + 20, transform)
      ctx.fillStyle = fabricColor
      ctx.fillRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y)

      // Fabric texture (subtle crosshatch)
      ctx.strokeStyle = 'rgba(0,0,0,0.03)'
      ctx.lineWidth = 0.5
      const step = 4 * transform.scale
      if (step > 2) {
        for (let gx = tl.x; gx < br.x; gx += step) {
          ctx.beginPath()
          ctx.moveTo(gx, tl.y)
          ctx.lineTo(gx, br.y)
          ctx.stroke()
        }
        for (let gy = tl.y; gy < br.y; gy += step) {
          ctx.beginPath()
          ctx.moveTo(tl.x, gy)
          ctx.lineTo(br.x, gy)
          ctx.stroke()
        }
      }
    }

    // --- Grid overlay ---
    if (showGrid && bounds) {
      ctx.strokeStyle = 'rgba(100,100,255,0.15)'
      ctx.lineWidth = 0.5
      const gridMm = 10  // 10mm grid
      const gridEu = gridMm * 10
      const startX = Math.floor(bounds.minX / gridEu) * gridEu
      const startY = Math.floor(bounds.minY / gridEu) * gridEu
      for (let gx = startX; gx <= bounds.maxX; gx += gridEu) {
        const p1 = toCanvas(gx, bounds.minY, transform)
        const p2 = toCanvas(gx, bounds.maxY, transform)
        ctx.beginPath()
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        ctx.stroke()
      }
      for (let gy = startY; gy <= bounds.maxY; gy += gridEu) {
        const p1 = toCanvas(bounds.minX, gy, transform)
        const p2 = toCanvas(bounds.maxX, gy, transform)
        ctx.beginPath()
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        ctx.stroke()
      }
    }

    // --- Stitch paths (with player progress support) ---
    const maxStitchesToDraw = Math.floor(playProgress * totalStitchPoints)
    let stitchesDrawn = 0
    let needlePos = null // track needle position for player indicator

    for (const shape of shapes) {
      if (stitchesDrawn >= maxStitchesToDraw) break

      // Resolve color from colorIndex → colors array, or use shape.color directly
      let strokeColor = shape.color || '#000000'
      if (Array.isArray(colors) && colors.length > 0 && shape.colorIndex !== undefined) {
        const c = colors[shape.colorIndex]
        if (c) {
          strokeColor = typeof c === 'string' ? c : (c.hex || `rgb(${c.r},${c.g},${c.b})`)
        }
      }
      // On light fabric, boost dark colors for visibility. On dark fabric, show as-is.
      if (strokeColor.startsWith('#') && strokeColor.length === 7) {
        const fr = parseInt(fabricColor.slice(1,3), 16) || 0
        const fg = parseInt(fabricColor.slice(3,5), 16) || 0
        const fb = parseInt(fabricColor.slice(5,7), 16) || 0
        const fabricLum = 0.299 * fr + 0.587 * fg + 0.114 * fb
        if (fabricLum > 128) {
          // Light fabric: boost dark thread colors for visibility
          const r = parseInt(strokeColor.slice(1,3), 16)
          const g = parseInt(strokeColor.slice(3,5), 16)
          const b = parseInt(strokeColor.slice(5,7), 16)
          const lum = 0.299 * r + 0.587 * g + 0.114 * b
          if (lum < 80) {
            const boost = 1.8
            const nr = Math.min(255, Math.round(r * boost + 30))
            const ng = Math.min(255, Math.round(g * boost + 30))
            const nb = Math.min(255, Math.round(b * boost + 30))
            strokeColor = `#${nr.toString(16).padStart(2,'0')}${ng.toString(16).padStart(2,'0')}${nb.toString(16).padStart(2,'0')}`
          }
        }
      }
      ctx.strokeStyle = strokeColor
      ctx.lineWidth = Math.max(1, 1.5 * transform.scale / 5)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      for (const seg of shape.segments) {
        if (seg.type !== 'stitch' || seg.points.length < 2) continue
        if (stitchesDrawn >= maxStitchesToDraw) break

        ctx.beginPath()
        const p0 = toCanvas(seg.points[0][0], seg.points[0][1], transform)
        ctx.moveTo(p0.x, p0.y)
        stitchesDrawn++
        needlePos = p0

        for (let i = 1; i < seg.points.length; i++) {
          if (stitchesDrawn >= maxStitchesToDraw) break
          const p = toCanvas(seg.points[i][0], seg.points[i][1], transform)
          ctx.lineTo(p.x, p.y)
          stitchesDrawn++
          needlePos = p
        }
        ctx.stroke()
      }
    }

    // --- Needle position indicator (when playing or scrubbing) ---
    if (needlePos && playProgress < 1.0) {
      ctx.fillStyle = '#ff3333'
      ctx.beginPath()
      ctx.arc(needlePos.x, needlePos.y, 4, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(needlePos.x, needlePos.y, 4, 0, Math.PI * 2)
      ctx.stroke()
    }

    // --- Jump stitches (red dashed) ---
    if (showJumps && jumps) {
      for (const jump of jumps) {
        if (deletedJumps.has(jump.id)) continue

        const from = toCanvas(jump.from[0], jump.from[1], transform)
        const to = toCanvas(jump.to[0], jump.to[1], transform)

        const isSelected = selectedJump === jump.id
        const isHovered = hoveredJump === jump.id

        ctx.strokeStyle = isSelected ? '#ff0' : isHovered ? '#ff6b6b' : 'rgba(255,50,50,0.7)'
        ctx.lineWidth = isSelected ? 3 : isHovered ? 2.5 : 1.5
        ctx.setLineDash(isSelected ? [8, 4] : [4, 4])

        ctx.beginPath()
        ctx.moveTo(from.x, from.y)
        ctx.lineTo(to.x, to.y)
        ctx.stroke()
        ctx.setLineDash([])

        // Jump endpoints
        const dotR = isSelected ? 5 : 3
        ctx.fillStyle = isSelected ? '#ff0' : 'rgba(255,50,50,0.8)'
        ctx.beginPath()
        ctx.arc(from.x, from.y, dotR, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.arc(to.x, to.y, dotR, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // --- Trim points (red diamonds) ---
    if (showTrims && trims) {
      for (const trim of trims) {
        const p = toCanvas(trim.position[0], trim.position[1], transform)
        const s = 5

        ctx.fillStyle = '#ff3366'
        ctx.beginPath()
        ctx.moveTo(p.x, p.y - s)
        ctx.lineTo(p.x + s, p.y)
        ctx.lineTo(p.x, p.y + s)
        ctx.lineTo(p.x - s, p.y)
        ctx.closePath()
        ctx.fill()

        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 1
        ctx.stroke()
      }
    }

  }, [shapes, jumps, trims, bounds, zoom, pan, showJumps, showTrims, showGrid,
      selectedJump, hoveredJump, deletedJumps, getTransform, toCanvas,
      playProgress, totalStitchPoints, fabricColor])

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      // Trigger re-render
      setZoom(z => z)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    setZoom(z => Math.max(0.1, Math.min(10, z * delta)))
  }, [])

  // Pan handlers
  const handleMouseDown = useCallback((e) => {
    if (e.button === 1 || e.button === 0 && e.shiftKey) {
      setIsPanning(true)
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
      e.preventDefault()
    }
  }, [pan])

  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container || !jumps) return

    if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y })
      return
    }

    // Hover detection on jumps
    const rect = container.getBoundingClientRect()
    const cw = rect.width
    const ch = rect.height
    const transform = getTransform(cw, ch)
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top

    let nearestJump = null
    let nearestDist = 15  // pixel threshold

    for (const jump of jumps) {
      if (deletedJumps.has(jump.id)) continue
      const from = toCanvas(jump.from[0], jump.from[1], transform)
      const to = toCanvas(jump.to[0], jump.to[1], transform)

      // Distance from point to line segment
      const dist = pointToSegmentDist(mx, my, from.x, from.y, to.x, to.y)
      if (dist < nearestDist) {
        nearestDist = dist
        nearestJump = jump.id
      }
    }

    setHoveredJump(nearestJump)
    canvas.style.cursor = nearestJump ? 'pointer' : isPanning ? 'grabbing' : 'default'
  }, [isPanning, panStart, jumps, deletedJumps, getTransform, toCanvas])

  const handleMouseUp = useCallback(() => {
    setIsPanning(false)
  }, [])

  // Click to select jump
  const handleClick = useCallback((e) => {
    if (hoveredJump) {
      setSelectedJump(selectedJump === hoveredJump ? null : hoveredJump)
    } else {
      setSelectedJump(null)
    }
  }, [hoveredJump, selectedJump])

  // Delete selected jump
  const handleDeleteJump = useCallback(() => {
    if (selectedJump) {
      setDeletedJumps(prev => new Set([...prev, selectedJump]))
      setSelectedJump(null)
    }
  }, [selectedJump])

  // Undo deleted jump
  const handleUndoDelete = useCallback(() => {
    setDeletedJumps(prev => {
      const next = new Set(prev)
      const last = [...next].pop()
      if (last) next.delete(last)
      return next
    })
  }, [])

  // Reset view
  const handleResetView = useCallback(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [])

  // Attach wheel listener with passive: false
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  // Download PES
  const handleDownload = useCallback(() => {
    if (!pesBase64) return
    const link = document.createElement('a')
    link.href = `data:application/octet-stream;base64,${pesBase64}`
    link.download = filename
    link.click()
  }, [pesBase64, filename])

  // Active stats (accounting for deleted jumps)
  const activeStats = useMemo(() => {
    if (!stats) return null
    return {
      ...stats,
      jumpCount: stats.jumpCount - deletedJumps.size,
      trimCount: stats.trimCount  // trims don't change from jump deletion
    }
  }, [stats, deletedJumps])

  if (!previewData) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-900 text-slate-400">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p>Generating stitch preview...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-slate-900 select-none">
      {/* Top toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-800/90 border-b border-slate-700 text-xs">
        <div className="flex items-center gap-2">
          <button 
            onClick={onClose}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md transition-colors flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Canvas
          </button>

          <div className="w-px h-5 bg-slate-600"></div>

          <span className="text-slate-400 font-medium tracking-wide uppercase">Stitch Preview</span>
        </div>

        <div className="flex items-center gap-2">
          {/* View toggles */}
          <button
            onClick={() => setShowJumps(!showJumps)}
            className={`px-2 py-1 rounded text-xs transition-colors ${
              showJumps ? 'bg-red-900/50 text-red-300 border border-red-700' : 'bg-slate-700 text-slate-400'
            }`}
          >
            Jumps
          </button>
          <button
            onClick={() => setShowTrims(!showTrims)}
            className={`px-2 py-1 rounded text-xs transition-colors ${
              showTrims ? 'bg-pink-900/50 text-pink-300 border border-pink-700' : 'bg-slate-700 text-slate-400'
            }`}
          >
            Trims
          </button>
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`px-2 py-1 rounded text-xs transition-colors ${
              showGrid ? 'bg-blue-900/50 text-blue-300 border border-blue-700' : 'bg-slate-700 text-slate-400'
            }`}
          >
            Grid
          </button>

          <div className="w-px h-5 bg-slate-600"></div>

          {/* Fabric color selector */}
          <span className="text-slate-500 text-xs">Fabric:</span>
          {[
            { color: '#ffffff', label: 'White' },
            { color: '#f5f0e8', label: 'Cream' },
            { color: '#1a1a1a', label: 'Black' },
            { color: '#2c3e50', label: 'Navy' },
            { color: '#c0392b', label: 'Red' },
            { color: '#f5deb3', label: 'Wheat' },
          ].map(f => (
            <button
              key={f.color}
              title={f.label}
              onClick={() => setFabricColor(f.color)}
              className={`w-5 h-5 rounded-full border-2 transition-all ${
                fabricColor === f.color ? 'border-blue-400 scale-110' : 'border-slate-600 hover:border-slate-400'
              }`}
              style={{ backgroundColor: f.color }}
            />
          ))}

          <div className="w-px h-5 bg-slate-600"></div>

          {/* Zoom controls */}
          <button onClick={() => setZoom(z => Math.min(10, z * 1.3))}
            className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded">+</button>
          <span className="text-slate-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.max(0.1, z / 1.3))}
            className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded">−</button>
          <button onClick={handleResetView}
            className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded">Fit</button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Canvas area */}
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleClick}
        >
          <canvas ref={canvasRef} className="absolute inset-0" />

          {/* Selected jump tooltip */}
          {selectedJump && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-800 border border-yellow-500/50 rounded-lg px-4 py-2.5 shadow-xl flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full animate-pulse"></div>
                <span className="text-yellow-200 text-sm font-medium">Jump stitch selected</span>
              </div>
              <button
                onClick={handleDeleteJump}
                className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white text-xs font-medium rounded-md transition-colors"
              >
                Remove Jump
              </button>
              <button
                onClick={() => setSelectedJump(null)}
                className="px-2 py-1 text-slate-400 hover:text-slate-200 text-xs"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Pan hint */}
          {playProgress >= 1.0 && !isPlaying && (
            <div className="absolute bottom-14 right-4 text-slate-500 text-xs">
              Shift+drag to pan • Scroll to zoom • Click jumps to edit
            </div>
          )}

          {/* Stitch Player Controls - inside canvas container */}
          {totalStitchPoints > 0 && (
            <div className="absolute bottom-0 left-0 right-0 bg-slate-800/95 border-t border-slate-700 px-4 py-2 flex items-center gap-3 z-10">
            {/* Play/Pause button */}
            <button
              onClick={() => {
                if (isPlaying) {
                  setIsPlaying(false)
                } else {
                  if (playProgress >= 1.0) setPlayProgress(0)
                  setIsPlaying(true)
                }
              }}
              className="w-8 h-8 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white rounded-md transition-colors"
            >
              {isPlaying ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21"/></svg>
              )}
            </button>

            {/* Progress bar / scrubber */}
            <input
              type="range"
              min="0"
              max="1000"
              value={Math.floor(playProgress * 1000)}
              onChange={(e) => {
                setIsPlaying(false)
                setPlayProgress(parseInt(e.target.value) / 1000)
              }}
              className="flex-1 h-1.5 bg-slate-600 rounded-full appearance-none cursor-pointer accent-blue-500"
            />

            {/* Stitch counter */}
            <span className="text-slate-400 text-xs font-mono w-28 text-right">
              {Math.floor(playProgress * totalStitchPoints).toLocaleString()} / {totalStitchPoints.toLocaleString()}
            </span>

            {/* Speed control */}
            <select
              value={playSpeed}
              onChange={(e) => setPlaySpeed(Number(e.target.value))}
              className="bg-slate-700 text-slate-300 text-xs rounded px-2 py-1 border border-slate-600"
            >
              <option value={0.5}>0.5x</option>
              <option value={1}>1x</option>
              <option value={2}>2x</option>
              <option value={5}>5x</option>
              <option value={10}>10x</option>
              <option value={50}>50x</option>
            </select>

            {/* Reset button */}
            <button
              onClick={() => { setIsPlaying(false); setPlayProgress(1.0) }}
              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors"
            >
              Show All
            </button>
          </div>
        )}
        </div>

        {/* Stats panel */}
        <div className="w-56 bg-slate-800/95 border-l border-slate-700 p-4 flex flex-col gap-4 overflow-y-auto">
          {/* Stitch stats */}
          <div>
            <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Statistics</h3>
            <div className="space-y-2">
              <StatRow label="Stitches" value={activeStats?.stitchCount?.toLocaleString()} />
              <StatRow label="Jumps" value={activeStats?.jumpCount} 
                color={activeStats?.jumpCount > 20 ? 'text-red-400' : 'text-green-400'} />
              <StatRow label="Trims" value={activeStats?.trimCount}
                color={activeStats?.trimCount > 15 ? 'text-yellow-400' : 'text-green-400'} />
              <StatRow label="Colors" value={activeStats?.colorCount} />
              <StatRow label="Shapes" value={activeStats?.shapeCount} />
            </div>
          </div>

          {/* Dimensions */}
          <div>
            <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Dimensions</h3>
            <div className="space-y-2">
              <StatRow label="Width" value={`${activeStats?.widthMm}mm`} />
              <StatRow label="Height" value={`${activeStats?.heightMm}mm`} />
              <StatRow label="Thread" value={`${activeStats?.totalLengthM}m`} />
              <StatRow label="Est. Time" value={`~${activeStats?.estimatedMinutes} min`} />
            </div>
          </div>

          {/* Colors */}
          <div>
            <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Thread Colors</h3>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(colors) && colors.map((color, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-700/50 px-2 py-1 rounded">
                  <div className="w-4 h-4 rounded-sm border border-slate-500" style={{ backgroundColor: color }}></div>
                  <span className="text-slate-300 text-xs font-mono">{color}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div>
            <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Legend</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-black"></div>
                <span className="text-slate-300">Stitches</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 border-t-2 border-dashed border-red-500"></div>
                <span className="text-slate-300">Jump stitches</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-pink-500 rotate-45 transform scale-75"></div>
                <span className="text-slate-300">Trim points</span>
              </div>
            </div>
          </div>

          {/* Edit actions */}
          {deletedJumps.size > 0 && (
            <div>
              <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">
                Edits ({deletedJumps.size})
              </h3>
              <button
                onClick={handleUndoDelete}
                className="w-full px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-md transition-colors mb-2"
              >
                Undo Last Delete
              </button>
              <button
                onClick={() => setDeletedJumps(new Set())}
                className="w-full px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-md transition-colors"
              >
                Reset All Edits
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="mt-auto space-y-2">
            <button
              onClick={handleDownload}
              disabled={!pesBase64}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-all shadow-lg"
            >
              Download {filename?.split('.').pop()?.toUpperCase()}
            </button>

            {deletedJumps.size > 0 && onReExport && (
              <button
                onClick={() => onReExport(deletedJumps)}
                className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Re-export with Edits
              </button>
            )}

            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors"
            >
              Back to Canvas
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}


// --- Helper Components ---

function StatRow({ label, value, color = 'text-slate-200' }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-400 text-xs">{label}</span>
      <span className={`text-sm font-mono font-medium ${color}`}>{value ?? '—'}</span>
    </div>
  )
}


// --- Geometry Helpers ---

function pointToSegmentDist(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy

  if (lenSq === 0) {
    const ddx = px - x1
    const ddy = py - y1
    return Math.sqrt(ddx * ddx + ddy * ddy)
  }

  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))

  const closestX = x1 + t * dx
  const closestY = y1 + t * dy
  const ddx = px - closestX
  const ddy = py - closestY
  return Math.sqrt(ddx * ddx + ddy * ddy)
}